import {
  HHAlert,
  HHBox,
  HHButton,
  HHCheckbox,
  HHDivider,
  HHFormControlLabel,
  HHStack,
  HHTextField,
  HHTypography,
} from '@hinge-health/react-component-library';
import { InputHTMLAttributes, useEffect, useRef, useState } from 'react';
import { ActionTypeDefinition } from '../domain/types';
import {
  ActionField,
  ActionFieldKind,
  ActionFieldValue,
  buildActionDraft,
  ConfigurationActionDraft,
  findActionType,
  formatStringList,
  isFieldObject,
  parseActionFields,
  parseNumberList,
  parseStringList,
  setFieldValue,
} from '../utils/configuration-action.utils';
import { NativeSelectField } from './native-select-field';

export const ACTION_BUILDER_TEST_ID = 'pdg-action-builder';
export const ACTION_BUILDER_ADD_TEST_ID = 'pdg-action-builder-add-button';
export const ACTION_BUILDER_EMPTY_TEST_ID = 'pdg-action-builder-empty';
export const ACTION_BUILDER_NO_CATALOG_TEST_ID =
  'pdg-action-builder-no-catalog';

export const actionCardTestId = (index: number): string =>
  `pdg-action-builder-action-${index}`;
export const actionRemoveTestId = (index: number): string =>
  `pdg-action-builder-action-${index}-remove-button`;
export const actionTypeSelectTestId = (index: number): string =>
  `pdg-action-builder-action-${index}-type-select`;
export const actionScopeSelectTestId = (index: number): string =>
  `pdg-action-builder-action-${index}-scope-select`;
export const actionFieldTestId = (index: number, key: string): string =>
  `pdg-action-builder-action-${index}-field-${key}`;
export const actionUnknownTypeTestId = (index: number): string =>
  `pdg-action-builder-action-${index}-unknown-type`;

/**
 * Id of the "Action N" heading, used to name the card's group. Every action
 * card repeats the same field and button labels ("Type", "Scope", "Remove"),
 * so without the group association a screen reader reads an undifferentiated
 * run of identical controls.
 */
export const actionCardHeadingId = (index: number): string =>
  `pdg-action-builder-action-${index}-heading`;

export const ACTION_BUILDER_NO_CATALOG_MESSAGE =
  'No action types are available right now, so actions cannot be added.';

export const UNSUPPORTED_VALUE_HELPER_TEXT =
  'This value cannot be edited here. It is kept as-is when you save.';

export interface ActionBuilderProps {
  actions: ConfigurationActionDraft[];
  actionTypes: ActionTypeDefinition[];
  onChange: (actions: ConfigurationActionDraft[]) => void;
  disabled?: boolean;
}

interface MetadataFieldProps {
  index: number;
  field: ActionField;
  value: ActionFieldValue | undefined;
  disabled: boolean;
  /**
   * Dotted key path from the metadata root, e.g. `validationConfig.minRowCount`.
   * Keeps test ids, input ids and input names unique once fields nest.
   */
  path: string;
  /** Whether the field sits inside an `object` field rather than at the root. */
  nested: boolean;
  /** `undefined` removes the key, e.g. when a number input is cleared. */
  onChange: (value: ActionFieldValue | undefined) => void;
}

/**
 * What each per-kind control receives. `testId` doubles as the input's DOM
 * `id`: `HHTextField` otherwise gives every instance the same hardcoded id,
 * which breaks the label and helper-text association for screen readers.
 */
interface FieldControlProps extends MetadataFieldProps {
  testId: string;
}

const isStructured = (value: ActionFieldValue | undefined): boolean =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Renders a stored metadata value as editable text for a `string` field.
 *
 * A value that PDGS added after this release shipped may be neither a list
 * nor a string — that degrades to text rather than throwing.
 */
const toTextValue = (value: ActionFieldValue | undefined): string => {
  if (Array.isArray(value)) {
    return formatStringList(value);
  }

  return value === null || value === undefined ? '' : String(value);
};

const toNumberInputValue = (value: ActionFieldValue | undefined): string =>
  typeof value === 'number' || typeof value === 'string' ? String(value) : '';

const parseNumberInput = (text: string): number | undefined => {
  if (text.trim() === '') {
    return undefined;
  }

  const parsed = Number(text);

  return Number.isFinite(parsed) ? parsed : undefined;
};

const sameValue = (a: unknown, b: unknown): boolean =>
  JSON.stringify(a) === JSON.stringify(b);

// A native fieldset/legend pair names the group for assistive tech, so the
// sub-fields read as "Validation Config, Min Row Count" rather than as loose
// inputs that happen to sit next to each other.
const ObjectFieldControl = ({
  index,
  field,
  value,
  disabled,
  path,
  testId,
  onChange,
}: FieldControlProps): JSX.Element => {
  const objectValue = isFieldObject(value) ? value : {};

  return (
    <HHBox
      component="fieldset"
      data-testid={testId}
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        margin: 0,
        paddingX: 2,
        paddingTop: 1,
        paddingBottom: 2,
        width: '100%',
        minWidth: 0,
      }}
    >
      <HHTypography
        hhVariant="variant-bypass"
        component="legend"
        variant="subtitle2"
        sx={{ paddingX: 0.5 }}
      >
        {field.label}
      </HHTypography>
      <HHStack direction="row" gap={2} flexWrap="wrap">
        {field.fields.map(child => (
          <MetadataField
            key={child.key}
            index={index}
            field={child}
            value={objectValue[child.key]}
            disabled={disabled}
            path={`${path}.${child.key}`}
            nested
            onChange={(next): void =>
              onChange(setFieldValue(objectValue, child.key, next))
            }
          />
        ))}
      </HHStack>
    </HHBox>
  );
};

const BooleanFieldControl = ({
  field,
  value,
  disabled,
  path,
  testId,
  onChange,
}: FieldControlProps): JSX.Element => {
  // `HHCheckbox` forwards `inputProps` to the underlying `<input>`, which is
  // the element tests and assistive tech interact with — the root span would
  // swallow both the test id and the label association.
  const checkboxInputProps = {
    name: path,
    'aria-label': field.label,
    'data-testid': testId,
  } as InputHTMLAttributes<HTMLInputElement>;

  return (
    <HHFormControlLabel
      label={field.label}
      control={
        <HHCheckbox
          hhVariant="variant-bypass"
          checked={value === true}
          disabled={disabled}
          onChange={(event): void => onChange(event.target.checked)}
          inputProps={checkboxInputProps}
        />
      }
    />
  );
};

// Inside an object a blank choice is how the operator says "not set":
// without it, a nested block could never be cleared once a strategy had been
// picked, and a half-filled block fails PDGS validation.
const SelectFieldControl = ({
  field,
  value,
  disabled,
  path,
  nested,
  testId,
  onChange,
}: FieldControlProps): JSX.Element => (
  <NativeSelectField
    label={field.label}
    name={path}
    testId={testId}
    disabled={disabled}
    value={typeof value === 'string' ? value : ''}
    includeBlank={nested}
    blankLabel="Not set"
    options={field.options.map(option => ({ value: option, label: option }))}
    onChange={(next): void =>
      onChange(nested && next === '' ? undefined : next)
    }
  />
);

/**
 * A list edited as comma separated text.
 *
 * The text is held locally instead of being re-derived from the parsed list
 * on every keystroke: re-deriving it would swallow a trailing comma as soon
 * as it was typed, so a second entry could never be started. The local text
 * only resyncs when the list changes from outside (e.g. the action type
 * switches underneath it).
 */
const ListFieldControl = ({
  field,
  value,
  disabled,
  path,
  testId,
  onChange,
}: FieldControlProps): JSX.Element => {
  const parse =
    field.kind === 'numberArray' ? parseNumberList : parseStringList;
  const list = Array.isArray(value) ? value : [];
  const [text, setText] = useState(formatStringList(list));

  useEffect(() => {
    setText(current =>
      sameValue(parse(current), list) ? current : formatStringList(list),
    );
    // `list` is rebuilt every render; its contents are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(list), parse]);

  return (
    <HHTextField
      hhVariant="variant-bypass"
      variant="outlined"
      size="small"
      id={testId}
      label={field.label}
      value={text}
      disabled={disabled}
      required={field.required}
      helperText="Comma separated"
      onChange={(event): void => {
        setText(event.target.value);
        onChange(parse(event.target.value));
      }}
      inputProps={{
        name: path,
        'aria-label': field.label,
        'data-testid': testId,
      }}
      sx={{ minWidth: 220 }}
    />
  );
};

const describeRange = ({ min, max }: ActionField): string | undefined => {
  if (min !== undefined && max !== undefined) {
    return `${min} to ${max}`;
  }

  if (min !== undefined) {
    return `at least ${min}`;
  }

  return max === undefined ? undefined : `at most ${max}`;
};

/**
 * Spells out a number field's constraints, e.g. "Whole number, at least 1".
 *
 * The configuration form sets `noValidate`, so the input's `min`/`max`/`step`
 * attributes are never surfaced by the browser. Without this the operator only
 * learns the range when PDGS rejects the save. Wired to the input through the
 * same `aria-describedby` the unique `id` sets up.
 */
export const describeNumberConstraints = (
  field: ActionField,
): string | undefined => {
  const parts = [
    field.integer ? 'whole number' : undefined,
    describeRange(field),
  ].filter((part): part is string => part !== undefined);

  if (parts.length === 0) {
    return undefined;
  }

  const text = parts.join(', ');

  return text.charAt(0).toUpperCase() + text.slice(1);
};

const NumberFieldControl = ({
  field,
  value,
  disabled,
  path,
  testId,
  onChange,
}: FieldControlProps): JSX.Element => (
  <HHTextField
    hhVariant="variant-bypass"
    variant="outlined"
    size="small"
    type="number"
    id={testId}
    label={field.label}
    value={toNumberInputValue(value)}
    disabled={disabled}
    required={field.required}
    helperText={describeNumberConstraints(field)}
    onChange={(event): void => onChange(parseNumberInput(event.target.value))}
    inputProps={{
      name: path,
      'aria-label': field.label,
      'data-testid': testId,
      min: field.min,
      max: field.max,
      step: field.integer ? 1 : 'any',
    }}
    sx={{ minWidth: 220 }}
  />
);

/**
 * A value this form cannot edit without destroying it: every `record` field
 * (its keys are authored per provider), and a structured value stored under
 * a field the catalog describes as text. Shown as JSON, never editable, and
 * written back exactly as loaded. `onChange` is deliberately never called.
 */
const ReadOnlyFieldControl = ({
  field,
  value,
  path,
  testId,
}: FieldControlProps): JSX.Element => (
  <HHTextField
    hhVariant="variant-bypass"
    variant="outlined"
    size="small"
    id={testId}
    label={field.label}
    value={value === undefined ? '' : JSON.stringify(value)}
    disabled
    helperText={UNSUPPORTED_VALUE_HELPER_TEXT}
    inputProps={{
      name: path,
      'aria-label': field.label,
      'data-testid': testId,
      readOnly: true,
    }}
    sx={{ minWidth: 220 }}
  />
);

const TextFieldControl = (props: FieldControlProps): JSX.Element => {
  const { field, value, disabled, path, testId, onChange } = props;

  // A kind this release does not know yet degrades to `string`; if the
  // stored value is structured, editing it as text would destroy it.
  if (isStructured(value)) {
    return <ReadOnlyFieldControl {...props} />;
  }

  return (
    <HHTextField
      hhVariant="variant-bypass"
      variant="outlined"
      size="small"
      id={testId}
      label={field.label}
      value={toTextValue(value)}
      disabled={disabled}
      required={field.required}
      onChange={(event): void => onChange(event.target.value)}
      inputProps={{
        name: path,
        'aria-label': field.label,
        'data-testid': testId,
      }}
      sx={{ minWidth: 220 }}
    />
  );
};

/**
 * One control per catalog field kind. Typed as a total map, so adding a kind
 * to `ACTION_FIELD_KINDS` fails the build until it has a control here.
 */
const FIELD_CONTROLS: Record<
  ActionFieldKind,
  (props: FieldControlProps) => JSX.Element
> = {
  string: TextFieldControl,
  number: NumberFieldControl,
  boolean: BooleanFieldControl,
  select: SelectFieldControl,
  stringArray: ListFieldControl,
  numberArray: ListFieldControl,
  object: ObjectFieldControl,
  record: ReadOnlyFieldControl,
};

const MetadataField = (props: MetadataFieldProps): JSX.Element => {
  const Control = FIELD_CONTROLS[props.field.kind];

  return (
    <Control {...props} testId={actionFieldTestId(props.index, props.path)} />
  );
};

/**
 * Editor for a configuration's `actions` array.
 *
 * Every choice on offer comes from `pdgActionsCatalog`: the type dropdown
 * lists the catalog's action types, the scope dropdown lists that type's
 * declared scopes, and the metadata inputs are generated from its field
 * schemas. Nothing about a specific action type is hardcoded here, so PDGS
 * can add one without a frontend release.
 */
export const ActionBuilder = ({
  actions,
  actionTypes,
  onChange,
  disabled = false,
}: ActionBuilderProps): JSX.Element => {
  const typeOptions = actionTypes.map(actionType => ({
    value: actionType.type,
    label: actionType.label,
  }));

  const addButtonRef = useRef<HTMLButtonElement>(null);
  const removeButtonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  /** Row whose Remove button should take focus after the next removal. */
  const pendingFocusIndex = useRef<number | null>(null);

  useEffect(() => {
    const index = pendingFocusIndex.current;

    if (index === null) {
      return;
    }

    pendingFocusIndex.current = null;

    // Only step in when the removal actually dropped focus. If the operator
    // was working somewhere else in the form when the row went, taking focus
    // off them would be its own bug.
    if (document.activeElement !== document.body) {
      return;
    }

    // Removing the first row leaves no row above it, so Add is the nearest
    // control that still means anything.
    const target =
      (index >= 0 ? removeButtonRefs.current[index] : null) ??
      addButtonRef.current;

    target?.focus();
  }, [actions]);

  const replaceAction = (
    index: number,
    action: ConfigurationActionDraft,
  ): void => {
    onChange(actions.map((existing, i) => (i === index ? action : existing)));
  };

  const handleTypeChange = (index: number, type: string): void => {
    // Metadata is per-type, so switching type rebuilds the row from the new
    // type's schema and defaults rather than carrying over fields that no
    // longer exist on it. The row keeps its `uid` through the rebuild: it is
    // still the same row to the operator, so it should not remount.
    replaceAction(index, {
      ...buildActionDraft(findActionType(actionTypes, type)),
      uid: actions[index].uid,
    });
  };

  const handleScopeChange = (index: number, scope: string): void => {
    replaceAction(index, { ...actions[index], scope });
  };

  const handleMetadataChange = (
    index: number,
    key: string,
    value: ActionFieldValue | undefined,
  ): void => {
    const action = actions[index];

    replaceAction(index, {
      ...action,
      metadata: setFieldValue(action.metadata, key, value),
    });
  };

  const handleRemove = (index: number): void => {
    // The Remove button that was just activated belongs to the row being
    // unmounted, so focus would otherwise fall to <body> and a keyboard or
    // screen-reader user would lose their place in the form. Hand it to the
    // row above, or to Add when the first row goes.
    pendingFocusIndex.current = index - 1;
    onChange(actions.filter((_action, i) => i !== index));
  };

  const handleAdd = (): void => {
    onChange([...actions, buildActionDraft(actionTypes[0])]);
  };

  return (
    <HHStack gap={2} data-testid={ACTION_BUILDER_TEST_ID}>
      <HHTypography hhVariant="variant-bypass" variant="h3" fontSize="1.125rem">
        Actions
      </HHTypography>

      {actionTypes.length === 0 && (
        <HHAlert
          hhVariant="variant-bypass"
          variant="outlined"
          severity="warning"
          data-testid={ACTION_BUILDER_NO_CATALOG_TEST_ID}
        >
          {ACTION_BUILDER_NO_CATALOG_MESSAGE}
        </HHAlert>
      )}

      {actions.length === 0 && (
        <HHTypography
          hhVariant="variant-bypass"
          variant="body2"
          color="textSecondary"
          data-testid={ACTION_BUILDER_EMPTY_TEST_ID}
        >
          No actions yet. Add one to run it when this rule matches.
        </HHTypography>
      )}

      {actions.map((action, index) => {
        const actionType = findActionType(actionTypes, action.type);
        const fields = parseActionFields(actionType?.fields ?? []);

        return (
          <HHBox
            key={action.uid}
            role="group"
            aria-labelledby={actionCardHeadingId(index)}
            data-testid={actionCardTestId(index)}
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              padding: 2,
            }}
          >
            <HHStack gap={2}>
              <HHStack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <HHTypography
                  hhVariant="variant-bypass"
                  variant="subtitle1"
                  id={actionCardHeadingId(index)}
                >
                  {`Action ${index + 1}`}
                </HHTypography>
                <HHButton
                  hhVariant="variant-bypass"
                  variant="text"
                  color="error"
                  size="small"
                  disabled={disabled}
                  ref={(node): void => {
                    removeButtonRefs.current[index] = node;
                  }}
                  onClick={(): void => handleRemove(index)}
                  data-testid={actionRemoveTestId(index)}
                >
                  Remove
                </HHButton>
              </HHStack>

              <HHStack direction="row" gap={2} flexWrap="wrap">
                <NativeSelectField
                  label="Type"
                  name={`action-${index}-type`}
                  testId={actionTypeSelectTestId(index)}
                  value={action.type}
                  options={typeOptions}
                  disabled={disabled}
                  onChange={(type): void => handleTypeChange(index, type)}
                />
                <NativeSelectField
                  label="Scope"
                  name={`action-${index}-scope`}
                  testId={actionScopeSelectTestId(index)}
                  value={action.scope}
                  options={(actionType?.scopes ?? []).map(scope => ({
                    value: scope,
                    label: scope,
                  }))}
                  disabled={disabled}
                  onChange={(scope): void => handleScopeChange(index, scope)}
                />
              </HHStack>

              {!actionType && (
                <HHTypography
                  hhVariant="variant-bypass"
                  variant="body2"
                  color="error"
                  data-testid={actionUnknownTypeTestId(index)}
                >
                  {`"${action.type}" is not in the action catalog. Pick a type to configure it.`}
                </HHTypography>
              )}

              {fields.length > 0 && (
                <>
                  <HHDivider />
                  <HHStack direction="row" gap={2} flexWrap="wrap">
                    {fields.map(field => (
                      <MetadataField
                        key={field.key}
                        index={index}
                        field={field}
                        value={action.metadata[field.key]}
                        disabled={disabled}
                        path={field.key}
                        nested={false}
                        onChange={(value): void =>
                          handleMetadataChange(index, field.key, value)
                        }
                      />
                    ))}
                  </HHStack>
                </>
              )}
            </HHStack>
          </HHBox>
        );
      })}

      <HHBox>
        <HHButton
          hhVariant="variant-bypass"
          variant="outlined"
          size="small"
          disabled={disabled || actionTypes.length === 0}
          ref={addButtonRef}
          onClick={handleAdd}
          data-testid={ACTION_BUILDER_ADD_TEST_ID}
        >
          + Add Action
        </HHButton>
      </HHBox>
    </HHStack>
  );
};
